import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FiColumns, FiEye, FiEyeOff, FiRotateCcw } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

function getStorageKey(tableKey) {
  try {
    const raw = localStorage.getItem('crm_user');
    const u = raw ? JSON.parse(raw) : null;
    const uid = u?.user_id || 'anon';
    return `col_prefs_${uid}_${tableKey}`;
  } catch {
    return `col_prefs_anon_${tableKey}`;
  }
}

/**
 * Hook to manage per-user column visibility preferences.
 *
 * @param {string} tableKey  - unique table identifier (e.g. 'students', 'leads')
 * @param {Array}  columns   - [{ key, label, locked?, defaultVisible? }]
 *
 * locked columns are always visible and cannot be toggled.
 * defaultVisible defaults to true when not specified.
 */
export function useColumnPreferences(tableKey, columns) {
  const storageKey = useMemo(() => getStorageKey(tableKey), [tableKey]);
  const migrationKey = useMemo(() => `${storageKey}__force_visible_v1`, [storageKey]);

  const allKeys = useMemo(() => columns.map((c) => c.key), [columns]);
  const lockedKeys = useMemo(
    () => new Set(columns.filter((c) => c.locked).map((c) => c.key)),
    [columns]
  );
  const defaultVisible = useMemo(
    () => new Set(columns.filter((c) => c.defaultVisible !== false).map((c) => c.key)),
    [columns]
  );
  const forceVisibleOnMigration = useMemo(
    () => new Set(columns.filter((c) => c.forceVisibleOnMigration).map((c) => c.key)),
    [columns]
  );

  const [visible, setVisible] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const saved = JSON.parse(raw);
        if (Array.isArray(saved)) {
          const set = new Set(saved);
          try {
            const migrated = localStorage.getItem(migrationKey) === '1';
            if (!migrated) {
              forceVisibleOnMigration.forEach((k) => set.add(k));
              localStorage.setItem(migrationKey, '1');
            }
          } catch {}
          lockedKeys.forEach((k) => set.add(k));
          return set;
        }
      }
    } catch {}
    return new Set(defaultVisible);
  });

  useEffect(() => {
    try {
      const arr = [...visible].filter((k) => allKeys.includes(k));
      localStorage.setItem(storageKey, JSON.stringify(arr));
    } catch {}
  }, [visible, storageKey, allKeys]);

  const isVisible = useCallback((key) => visible.has(key), [visible]);

  const toggleColumn = useCallback(
    (key) => {
      if (lockedKeys.has(key)) return;
      setVisible((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    },
    [lockedKeys]
  );

  const resetToDefault = useCallback(() => {
    setVisible(new Set(defaultVisible));
  }, [defaultVisible]);

  return { isVisible, toggleColumn, resetToDefault, visibleColumns: visible, columns };
}

/**
 * 3-dot column customizer dropdown button.
 *
 * Props: { columns, isVisible, toggleColumn, resetToDefault }
 */
export default function ColumnCustomizer({ columns, isVisible, toggleColumn, resetToDefault }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const toggleable = columns.filter((c) => !c.locked);
  const hiddenCount = toggleable.filter((c) => !isVisible(c.key)).length;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className="btn btn-sm btn-outline"
        onClick={() => setOpen((v) => !v)}
        title={t('common.columns', 'Columns')}
        style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 4 }}
      >
        <FiColumns size={14} />
        {hiddenCount > 0 && (
          <span style={{
            fontSize: 10, background: 'var(--primary)', color: '#fff',
            borderRadius: 10, padding: '1px 5px', lineHeight: '14px', fontWeight: 700
          }}>
            {hiddenCount}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '100%', marginTop: 4,
          background: 'var(--card-bg, #fff)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius, 8px)', boxShadow: '0 8px 24px rgba(0,0,0,.12)',
          zIndex: 1000, minWidth: 220, maxHeight: 400, overflow: 'auto',
          padding: '8px 0'
        }}>
          <div style={{
            padding: '6px 14px 8px', display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', borderBottom: '1px solid var(--border)'
          }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)' }}>
              {t('common.toggleColumns', 'Toggle Columns')}
            </span>
            <button
              type="button"
              onClick={() => { resetToDefault(); }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--primary)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 3,
                padding: '2px 4px'
              }}
              title={t('common.resetColumns', 'Reset to default')}
            >
              <FiRotateCcw size={12} /> {t('common.reset', 'Reset')}
            </button>
          </div>

          {toggleable.map((col) => {
            const on = isVisible(col.key);
            return (
              <label
                key={col.key}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 14px', cursor: 'pointer', fontSize: 13,
                  color: on ? 'var(--text)' : 'var(--text-muted)',
                  transition: 'background 0.15s'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-secondary, #f5f5f5)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggleColumn(col.key)}
                  style={{ accentColor: 'var(--primary)', width: 15, height: 15 }}
                />
                {on ? <FiEye size={13} /> : <FiEyeOff size={13} />}
                <span>{col.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
